import { Container, TextField, Button, Typography, Checkbox } from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import { Box } from '@mui/system';
import React, { FC, useCallback, useEffect, useMemo, useState, useRef } from 'react';

import { Page } from '../../components';
import { ConfirmationDialogue } from '../../components/modal/ConfirmationDialogue';
import { AdminTable } from '../../components/table/AdminTable';
import { IBankAccountsAPI } from '../../fetch';
import { useToastContext } from '../../hooks';
import { IBankAccount } from '../../models';
import { downloadFile, getDownloadFileName } from '../../helpers';

export const BankAccounts: FC = () => {
  // Non-state
  const { showToast } = useToastContext();

  // Local state
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  // If not false, this is the row the user seeks to delete
  const [isDeletingBankAccount, setIsDeletingBankAccount] = useState<false | IBankAccount>(false);

  // Local state from the network
  const [rows, setRows] = useState<IBankAccount[]>([]);

  const removeRow = (row: IBankAccount): void => {
    setRows((state: IBankAccount[]) => {
      const index = state.indexOf(row);
      const newState = state.slice();
      // Cut out the deleted row
      newState.splice(index, 1);
      return newState;
    });
  };

  const columns = useMemo(() => {
    return [
      {
        accessor: 'bankAccountName',
        Header: 'Bank Account Name',
        verticalAlignment: 'top',
        Cell: ({
          cell: {
            row: { original, index },
          },
        }: {
          cell: { row: { original: IBankAccount; index: number } };
        }) => {
          const [bankAccountName, setBankAccountName] = useState(original.name);

          return (
            <Box>
              <TextField
                fullWidth
                inputProps={{
                  className: 'bank-account-name-input',
                }}
                autoFocus
                placeholder="Bank Account Name"
                value={bankAccountName}
                onChange={event => setBankAccountName(event.target.value)}
                onBlur={() => {
                  setRows(state => {
                    const accountIndex = state.indexOf(original);

                    // Enable save button
                    if (state[accountIndex].name !== bankAccountName) {
                      setHasChanges(true);
                      state[accountIndex].isEdited = true;
                    }

                    // Store changes in preparation for batch save
                    state[accountIndex].name = bankAccountName;
                    return state;
                  });
                }}
              />
            </Box>
          );
        },
      },
      {
        accessor: 'instructions',
        Header: 'Wiring Instructions',
        overrideWidth: '500px',
        Cell: ({
          cell: {
            row: { original, index },
          },
        }: {
          cell: { row: { original: IBankAccount; index: number } };
        }) => {
          const [instructions, setInstructions] = useState(original.wiringInstructions);
          return (
            <Box width="100%">
              <TextField
                fullWidth
                multiline
                inputProps={{
                  id: `bank-account-wiring-instructions-${index}`,
                }}
                minRows={2}
                maxRows={10}
                placeholder="Wiring Instructions"
                value={instructions}
                onChange={event => setInstructions(event.target.value)}
                // Once the user leaves the input, take the changes they've written and commit
                // them to local state. This readies these changes for the ultimate batch save action.
                onBlur={() => {
                  setRows(state => {
                    const accountIndex = state.indexOf(original);

                    // Enable save button
                    if (state[accountIndex].wiringInstructions !== instructions) {
                      setHasChanges(true);
                      state[accountIndex].isEdited = true;
                    }

                    // Store changes in preparation for batch save
                    state[accountIndex].wiringInstructions = instructions;
                    return state;
                  });
                }}
              />
            </Box>
          );
        },
      },
      {
        accessor: 'businessName',
        Header: 'Legal Business Name',
        verticalAlignment: 'top',
        Cell: ({
          cell: {
            row: { original, index },
          },
        }: {
          cell: { row: { original: IBankAccount; index: number } };
        }) => {
          const [businessName, setBusinessName] = useState(original.legalBusinessName || '');

          return (
            <Box>
              <TextField
                fullWidth
                inputProps={{
                  id: `legal-business-name-input-${index}`,
                }}
                placeholder="Legal Business Name"
                value={businessName}
                onChange={event => setBusinessName(event.target.value)}
                onBlur={() => {
                  setRows(state => {
                    const accountIndex = state.indexOf(original);

                    // Enable save button
                    if (state[accountIndex].legalBusinessName !== businessName) {
                      setHasChanges(true);
                      state[accountIndex].isEdited = true;
                    }

                    // Store changes in preparation for batch save
                    state[accountIndex].legalBusinessName = businessName;
                    return state;
                  });
                }}
              />
            </Box>
          );
        },
      },
      {
        accessor: 'w9',
        Header: 'W-9',
        verticalAlignment: 'top',
        sort: false,
        Cell: ({
          cell: {
            row: { original },
          },
        }: {
          cell: { row: { original: IBankAccount } };
        }) => {
          const inputRef = useRef<HTMLInputElement>(null);
          const [isUploading, setIsUploading] = useState(false);

          // Check if fileName exists, otherwise fall back to a default display name
          const [fileName, setFileName] = useState<string>(original.fileName || 'Download File');

          useEffect(() => {
            setFileName(original.fileName || 'Download File');
          }, [original.fileName]);

          const handleUploadClick = () => {
            if (inputRef.current) {
              inputRef.current.click();
            }
          };

          const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            if (file) {
              setIsUploading(true);
              setIsSaving(true);
              try {
                const id = await IBankAccountsAPI.uploadW9(file);

                // Create updated rows with the new fileId and fileName
                const updatedRows = rows.map(account => {
                  if (account === original) {
                    return {
                      ...account,
                      fileId: id,
                      fileName: file.name,
                      isEdited: true,
                    };
                  } else {
                    return account;
                  }
                });

                // Update the state with the updated rows
                setRows(updatedRows);

                // Save all changes by calling upsert on the updated rows
                const newRows: IBankAccount[] = await IBankAccountsAPI.upsert(updatedRows);

                // Update the state with the new rows returned from the server
                setRows(newRows);

                // Reset save button to disabled
                setHasChanges(false);

                showToast('success', 'File uploaded and all changes saved successfully.');
              } catch (error) {
                const message = error instanceof Error ? error.message : 'Failed to upload file.';
                showToast('error', message);
              } finally {
                setIsUploading(false);
                setIsSaving(false);
              }
            }
          };

          const handleDownloadClick = async () => {
            if (original.fileId) {
              try {
                const response = await IBankAccountsAPI.downloadW9(
                  original.bankAccountId,
                  original.fileId
                );

                const fileName = getDownloadFileName(response) || 'w9_file.pdf';
                downloadFile(response.data, fileName);
              } catch (error) {
                const message = error instanceof Error ? error.message : 'Failed to download file.';
                showToast('error', message);
              }
            }
          };

          return (
            <Box display="flex" alignItems="center">
              <Button
                variant="contained"
                color="primary"
                onClick={handleUploadClick}
                disabled={isUploading || isSaving}
                startIcon={<UploadIcon />}
                style={{ marginRight: 8 }}
              >
                {original.fileId ? 'Replace W-9' : 'Upload W-9'}
              </Button>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                style={{ display: 'none' }}
                ref={inputRef}
                onChange={handleFileChange}
              />
              {original.fileId ? (
                <Button
                  variant="text"
                  color="primary"
                  onClick={handleDownloadClick}
                  disabled={isUploading || isSaving}
                >
                  {fileName}
                </Button>
              ) : (
                <Typography variant="body2" color="textSecondary" pl={0.5}>
                  No file uploaded
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        accessor: 'default',
        Header: 'Default',
        verticalAlignment: 'top',
        sort: false,
        Cell: ({
          cell: {
            row: { original },
          },
        }: {
          cell: { row: { original: IBankAccount } };
        }) => {
          const isChecked = original.isDefault || false;

          const handleChange = () => {
            setRows(state => {
              return state.map(account => {
                if (account.bankAccountId === original.bankAccountId) {
                  // If the clicked account is not already default, update it
                  if (!account.isDefault) {
                    return {
                      ...account,
                      isDefault: true,
                      isEdited: true,
                    };
                  }
                  // If it was already default, no change needed
                  return account;
                } else {
                  // If another account was default, unset it and mark as edited
                  if (account.isDefault) {
                    return {
                      ...account,
                      isDefault: false,
                      isEdited: true,
                    };
                  }
                  // For other accounts, no change
                  return account;
                }
              });
            });
            setHasChanges(true);
          };

          return (
            <Checkbox
              checked={isChecked}
              onChange={handleChange}
              inputProps={{
                id: `default-checkbox-${original.bankAccountId}`,
              }}
            />
          );
        },
      },
    ];
    // Ignore showToast
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasChanges, rows, isSaving]);

  useEffect(() => {
    fetchOnMount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const fetchOnMount = useCallback(async () => {
    setIsLoading(true);
    try {
      const accounts: IBankAccount[] = await IBankAccountsAPI.getAll();
      setRows(accounts);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : displayFailure('get', 'bank accounts');
      showToast('error', message);
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  return (
    <Page title="Bank Accounts">
      <Container maxWidth={false} disableGutters>
        <AdminTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          hasChanges={hasChanges}
          isSaving={isSaving}
          onAddFocusClass="bank-account-name-input"
          onAdd={() => {
            // Add new empty row
            setRows(state => state.concat(IBankAccount.getDefault()));
          }}
          onSave={async function upsertBankAccounts() {
            setIsSaving(true);
            try {
              const newRows: IBankAccount[] = await IBankAccountsAPI.upsert(rows);

              // After successful update, update UI
              setRows(newRows);

              // Reset save button to disabled
              setHasChanges(false);

              showToast('success', 'Successfully saved bank accounts.');
            } catch (error) {
              const message =
                error instanceof Error ? error.message : displayFailure('update', 'bank accounts');
              showToast('error', message);
            } finally {
              setIsSaving(false);
            }
          }}
          onDelete={(original, _index) => {
            if (original.bankAccountId === IBankAccount.getDefault().bankAccountId) {
              removeRow(original);
              showToast('info', 'Removed unsaved bank account.');
            } else {
              setIsDeletingBankAccount(original);
            }
          }}
        />
      </Container>

      {/** ConfirmationDialogue */}
      {!!isDeletingBankAccount && (
        <ConfirmationDialogue
          open
          id="delete-bank-account"
          title={`Delete Bank Account ${isDeletingBankAccount.name}`}
          onClose={() => setIsDeletingBankAccount(false)}
          onConfirm={async function deleteBankAccount() {
            // Check if modal is open and has proper data
            if (!isDeletingBankAccount) {
              return;
            }
            // If this is a new, not-yet-saved entry, it does not exist in the DB
            if (isDeletingBankAccount.bankAccountId === IBankAccount.getDefault().bankAccountId) {
              // So, deletion only consists of removal from local state
              removeRow(isDeletingBankAccount);
            } else {
              // Otherwise this entry already exists, hit the API to delete it
              try {
                await IBankAccountsAPI.delete(isDeletingBankAccount.bankAccountId);
                // Update state after only successful delete
                removeRow(isDeletingBankAccount);
                showToast('success', 'Successfully deleted bank account.');
              } catch (error) {
                const message =
                  error instanceof Error ? error.message : displayFailure('delete', 'bank account');
                showToast('error', message);
              }
            }

            // Close modal
            setIsDeletingBankAccount(false);
          }}
        />
      )}
    </Page>
  );
};

function displayFailure(operation: 'get' | 'update' | 'delete', resource: string): string {
  // Verify message ends with a period.
  const resource_ = resource.endsWith('.') ? resource.slice(0, resource.length - 1) : resource;

  return `Failed to ${operation} ${resource_}.`;
}
